<template>
	<div>
		<BlockEditorTestResult v-bind="props" v-on="listeners" :showTestResultModal.sync="isShowTestResultModal" />
	</div>
</template>

<script>
import { flattenPreserveArrays } from '@/utils/helpers';

import { mapGettersShorthand } from '@/store/utils/mappers';
import { getService } from '@/store/modules/apps/getters';
import {
	rootNode,
	actionContextNode,
	element,
	isOfTypeFeature,
	selectedWorkflowId,
	elementContextualData,
} from '@/store/modules/selectedWorkflow/getters';

import { editNode, isWebhook, isScheduledWebhook } from '@/store/modules/selectedNode/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { getNode, getNodeOutputLabelsByNodeId, getNodeTestResult } from '@/store/modules/nodes/getters';

import AppService from '@/utils/AppService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

import BlockEditorTestResult from '../BlockEditorTestResult';

import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { getPlacement } from '@/store/modules/placements/getters';

export default {
	name: 'TestResultsDataProvider',
	components: {
		BlockEditorTestResult,
	},
	props: {
		testBtnLoading: { type: Boolean },
		showTestResultModal: { type: Boolean },
		testResponseTruncate: { type: Function },
	},
	computed: {
		...mapGettersShorthand({
			element,
			getService,
			editNode,
			isWebhook,
			isScheduledWebhook,
			actionContextNode,
			selectedWorkflowId,
			canEditWorkflow,
			isOfTypeFeature,
			getNode,
			rootNode,
			getNodeOutputLabelsByNodeId,
			getNodeTestResult,
			elementContextualData,
			getPlacement,
		}),
		props() {
			const {
				appInfo,
				testResponse,
				testResultStatus,
				isTestError,
				node,
				hideTestBtn,
				hasEditPermissions,
				addNewStepAllowedFromTestResult,
				testResponseTruncate,
			} = this;
			return {
				...this.$attrs,
				appInfo,
				testResponse,
				testResultStatus,
				isTestError,
				node,
				hideTestBtn,
				hasEditPermissions,
				addNewStepAllowed: addNewStepAllowedFromTestResult,
				testResponseTruncate,
			};
		},
		listeners() {
			return { ...this.$listeners };
		},
		appInfo() {
			if (this.hasContextualDataFromElement) {
				return this.getService(this.getPlacement(this.element.placement).service);
			}
			return this.getService(this.relevantEditNode?.service);
		},
		testResponse() {
			if (this.hasContextualDataFromElement) {
				return this.elementContextualData | {};
			}
			if (this.editNode && this.relevantEditNode) {
				return flattenPreserveArrays(this.editNodeTestResponse);
			}
			return {};
		},
		testResultStatus() {
			if (this.hasContextualDataFromElement) {
				return TEST_RESULT_STATUSES.SUCCESS;
			}
			const node = this.getSourceOfRelevantNode();
			return node ? node.test_result_status : null;
		},
		isTestError() {
			return (
				this.testResponse.hasOwnProperty('error.message') ||
				this.testResponse.hasOwnProperty('error') ||
				this.testResponse.hasOwnProperty('Error') ||
				this.testResultStatus === TEST_RESULT_STATUSES.ERROR
			); // TODO change this to field 'error'
		},
		node() {
			return this.relevantEditNode;
		},
		hideTestBtn() {
			return (
				!!this.hasContextualDataFromElement ||
				(!this.canChangeContext && this.relevantNodeIsActionContextNodeOfButton) ||
				!this.hasEditPermissions
			);
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.selectedWorkflowId);
		},

		addNewStepAllowedFromTestResult() {
			return !this.isOfTypeFeature || !this.isWebhook;
		},

		relevantEditNode() {
			if (this.isWebhook && this.hasContextualDataFromElement) {
				// In case on context extracted without contextNode - only for isWebhook nodes.
				return this.rootNode;
			}
			return this.editNode; // todo: replace all
		},
		hasContextualDataFromElement() {
			// Don't remove the check for isWebhook ! so we know we are in the rootNode of a contextual action
			// Check that there isn't actionContextNode, and there is elementContextualData data
			return this.isWebhook && !this.actionContextNode && Object.keys(this.elementContextualData || {}).length > 0;
		},
		canChangeContext() {
			if (AppService.isServiceTestAgainBlocked(this.actionContextNode?.service_slug)) {
				return false;
			}
			return true;
		},
		relevantNodeIsActionContextNodeOfButton() {
			return this.relevantEditNode.id === this.actionContextNode?.id;
		},
		editNodeTestResponse() {
			const { id: nodeId } = this.relevantEditNode;
			const testResponseOutputLabels = this.getNodeOutputLabelsByNodeId(nodeId);
			const testResult = this.getNodeTestResult(nodeId);
			return WorkflowNodeService.replaceKeysWithOutputLabels(testResult, testResponseOutputLabels);
		},
		isShowTestResultModal: {
			get() {
				return this.showTestResultModal;
			},
			set(value) {
				this.$emit('update:showTestResultModal', value);
			},
		},
	},
	methods: {
		getSourceOfRelevantNode() {
			// we might want to do some validations on the node object came from server and not the one in the local state
			if (this.relevantEditNode) {
				const { id: nodeId } = this.relevantEditNode;
				const node = this.getNode(nodeId);
				return node;
			}
			return null;
		},
	},
};
</script>

<style scoped lang="scss"></style>
